import { Ref, ref, watch } from "vue";

import type {
  Controller,
  GeneralizedTaskId,
  ReleasableRef,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import type { UseProfile } from "@/composables/useProfile";
import { withRelease } from "@/ref";

/**
 * Scope restricting the personal task lists to personal tasks.
 *
 * The task list RPCs (`task.unplanned`, `task.upcoming`, `task.batch`) return
 * every task the user can see: native tasks plus the rows contributed by all
 * enabled task bindings. Native tasks belong to the personal scope; bound
 * rows belong to the org owning their workspace, so the filtering parses the
 * generalized task ids (graph object ids embed their workspace) and keeps
 * native tasks and the personal workspace's rows. Other orgs' tasks live in
 * their own org Tasks screens.
 */
export type TaskScope = {
  /**
   * Reactive scoped view of a list of generalized task ids. The returned
   * ref owns the filtering watcher — callers must `releaser.add(...)` it.
   */
  scopedTaskIds: (
    source: Ref<ReadonlyArray<GeneralizedTaskId> | undefined>
  ) => ReleasableRef<GeneralizedTaskId[] | undefined>;
};

export const usePersonalTaskScope = (
  controller: Controller,
  profile: Ref<UseProfile>
): TaskScope => {
  const workspaceIds = new Set<WorkspaceId>([
    profile.value.user.personal.workspaceId,
  ]);

  // Workspace of a generalized task id (null for native tasks). Memoized:
  // the same ids come back on every list refresh.
  const idWorkspaceCache = new Map<string, Promise<WorkspaceId | null>>();
  const workspaceOf = (id: GeneralizedTaskId): Promise<WorkspaceId | null> => {
    const cached = idWorkspaceCache.get(id);
    if (cached) return cached;
    const promise = (async () => {
      // Native CRDT task ids are not graph object ids.
      if (id.startsWith("task:")) return null;
      try {
        const parsed = await controller.graph.object.id.parse(id);
        return typeof parsed.workspace_id === "string"
          ? parsed.workspace_id
          : null;
      } catch {
        return null;
      }
    })();
    idWorkspaceCache.set(id, promise);
    return promise;
  };

  const belongsToScope = async (id: GeneralizedTaskId): Promise<boolean> => {
    const workspaceId = await workspaceOf(id);
    if (workspaceId === null) return true;
    return workspaceIds.has(workspaceId);
  };

  const scopedTaskIds = (
    source: Ref<ReadonlyArray<GeneralizedTaskId> | undefined>
  ): ReleasableRef<GeneralizedTaskId[] | undefined> => {
    const scoped = ref<GeneralizedTaskId[] | undefined>(undefined);
    let generation = 0;
    const stop = watch(
      source,
      async (ids) => {
        const currentGeneration = ++generation;
        if (!ids) {
          scoped.value = undefined;
          return;
        }
        const keep = await Promise.all(ids.map(belongsToScope));
        // A newer source value superseded this computation.
        if (currentGeneration !== generation) return;
        scoped.value = ids.filter((_, index) => keep[index]);
      },
      { immediate: true, deep: true }
    );
    return withRelease(scoped, stop);
  };

  return { scopedTaskIds };
};
